/**
 * Export utilities for CSV and PDF generation
 */

/**
 * Convert data to CSV format and trigger download
 */
export function exportToCSV(data: any[], filename: string) {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvContent = [
    headers.join(','), // Header row
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}

/**
 * Export emissions data to CSV
 */
export function exportEmissionsToCSV(emissions: any[], periodName: string) {
  const formattedData = emissions.map(e => ({
    'Source': e.source_name || 'N/A',
    'Category': e.category || 'N/A',
    'Scope': e.scope || 'N/A',
    'Activity Data': e.activity_data || 0,
    'Unit': e.unit || 'N/A',
    'Emission Factor': e.emission_factor || 0,
    'tCO2e': e.tco2e?.toFixed(2) || 0,
    'Date': new Date(e.created_at).toLocaleDateString(),
  }));

  exportToCSV(formattedData, `emissions-${periodName}-${new Date().toISOString().split('T')[0]}`);
}

/**
 * Generate PDF report (simplified version)
 * For production, consider using jsPDF or similar library
 */
export function exportToPDF(title: string, content: string) {
  // Create a printable HTML page
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    alert('Please allow popups to export PDF');
    return;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          padding: 40px;
          max-width: 800px;
          margin: 0 auto;
        }
        h1 {
          color: #1e3a5f;
          border-bottom: 2px solid #4a9d7e;
          padding-bottom: 10px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 12px;
          text-align: left;
        }
        th {
          background-color: #1e3a5f;
          color: white;
        }
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        @media print {
          body {
            padding: 20px;
          }
        }
      </style>
    </head>
    <body>
      ${content}
      <script>
        window.onload = function() {
          window.print();
          // Close after printing (optional)
          // window.onafterprint = function() { window.close(); };
        };
      </script>
    </body>
    </html>
  `);
  
  printWindow.document.close();
}

/**
 * Export emissions report to PDF
 */
export function exportEmissionsReportToPDF(
  emissions: any[],
  totalEmissions: number,
  periodName: string,
  scope1: number,
  scope2: number,
  scope3: number
) {
  const emissionsRows = emissions.map(e => `
    <tr>
      <td>${e.source_name || 'N/A'}</td>
      <td>${e.category || 'N/A'}</td>
      <td>${e.scope || 'N/A'}</td>
      <td>${e.tco2e?.toFixed(2) || 0}</td>
    </tr>
  `).join('');

  const content = `
    <h1>Carbon Emissions Report</h1>
    <p><strong>Period:</strong> ${periodName}</p>
    <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
    
    <h2>Summary</h2>
    <table>
      <tr>
        <th>Metric</th>
        <th>Value (tCO₂e)</th>
        <th>Percentage</th>
      </tr>
      <tr>
        <td><strong>Total Emissions</strong></td>
        <td><strong>${totalEmissions.toFixed(2)}</strong></td>
        <td><strong>100%</strong></td>
      </tr>
      <tr>
        <td>Scope 1</td>
        <td>${scope1.toFixed(2)}</td>
        <td>${((scope1 / totalEmissions) * 100).toFixed(1)}%</td>
      </tr>
      <tr>
        <td>Scope 2</td>
        <td>${scope2.toFixed(2)}</td>
        <td>${((scope2 / totalEmissions) * 100).toFixed(1)}%</td>
      </tr>
      <tr>
        <td>Scope 3</td>
        <td>${scope3.toFixed(2)}</td>
        <td>${((scope3 / totalEmissions) * 100).toFixed(1)}%</td>
      </tr>
    </table>

    <h2>Detailed Emissions</h2>
    <table>
      <thead>
        <tr>
          <th>Source</th>
          <th>Category</th>
          <th>Scope</th>
          <th>tCO₂e</th>
        </tr>
      </thead>
      <tbody>
        ${emissionsRows}
      </tbody>
    </table>

    <p style="margin-top: 40px; color: #666; font-size: 12px;">
      Generated by MyPA - Carbon Accounting Platform for African SMEs
    </p>
  `;

  exportToPDF(`Emissions Report - ${periodName}`, content);
}

